import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Shield, AlertTriangle, CheckCircle2, Eye, Lock, FileText } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GovernanceCenter = () => {
  const [metrics, setMetrics] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [metricsRes, auditRes] = await Promise.all([
        axios.get(`${API}/governance/metrics`),
        axios.get(`${API}/audit-logs`),
      ]);
      setMetrics(metricsRes.data);
      setAuditLogs(auditRes.data);
    } catch (error) {
      console.error('Error fetching governance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const ethicalPrinciples = [
    { name: 'Fairness', importance: 95, concern: 8, color: '#8b5cf6' },
    { name: 'Transparency', importance: 92, concern: 15, color: '#06b6d4' },
    { name: 'Accountability', importance: 88, concern: 12, color: '#f59e0b' },
    { name: 'Privacy', importance: 90, concern: 20, color: '#ef4444' },
    { name: 'Bias Mitigation', importance: 85, concern: 25, color: '#10b981' },
    { name: 'Robustness', importance: 82, concern: 10, color: '#6366f1' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="governance-page">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Governance Center
        </h1>
        <p className="text-sm sm:text-base text-gray-400">AI governance, compliance, and security monitoring</p>
      </div>

      {/* Overview Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
        <div className="metric-card" data-testid="security-metric">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center">
              <Shield className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Security Score</p>
              <h3 className="text-2xl font-bold text-white">{metrics?.security_score || 95.5}%</h3>
            </div>
          </div>
          <div className="w-full bg-gray-800 rounded-full h-2">
            <div
              className="bg-green-500 h-2 rounded-full"
              style={{ width: `${metrics?.security_score || 95.5}%` }}
            ></div>
          </div>
        </div>

        <div className="metric-card" data-testid="compliance-metric">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Compliance Status</p>
              <h3 className="text-xl font-bold text-white">{metrics?.compliance_status || 'Compliant'}</h3>
            </div>
          </div>
          <p className="text-green-400 text-sm">SOC 2 Type II</p>
        </div>

        <div className="metric-card" data-testid="violations-metric">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-lg bg-yellow-500/20 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-yellow-400" />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Policy Violations</p>
              <h3 className="text-2xl font-bold text-white">{metrics?.policy_violations || 2}</h3>
            </div>
          </div>
          <p className="text-yellow-400 text-sm">Last 30 days</p>
        </div>

        <div className="metric-card" data-testid="shadow-ai-metric">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-lg bg-red-500/20 flex items-center justify-center">
              <Eye className="w-6 h-6 text-red-400" />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Shadow AI Detected</p>
              <h3 className="text-2xl font-bold text-white">{metrics?.shadow_ai_detected || 0}</h3>
            </div>
          </div>
          <p className="text-gray-400 text-sm">Proactive monitoring</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="principles" className="w-full">
        <TabsList className="bg-white/5 border border-white/10" data-testid="governance-tabs">
          <TabsTrigger value="principles" className="data-[state=active]:bg-violet-500/20" data-testid="tab-principles">
            Ethical Principles
          </TabsTrigger>
          <TabsTrigger value="audit" className="data-[state=active]:bg-violet-500/20" data-testid="tab-audit">
            Audit Trail
          </TabsTrigger>
          <TabsTrigger value="compliance" className="data-[state=active]:bg-violet-500/20" data-testid="tab-compliance">
            Compliance
          </TabsTrigger>
        </TabsList>

        <TabsContent value="principles" className="mt-6">
          <div className="glass-panel p-6" data-testid="principles-heatmap">
            <h3 className="text-lg font-semibold text-white mb-6">Ethical AI Principles Heat Map</h3>
            <div className="space-y-4">
              {ethicalPrinciples.map((principle) => (
                <div key={principle.name} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-white font-medium">{principle.name}</span>
                    <div className="flex gap-4 text-sm">
                      <span className="text-green-400">Importance: {principle.importance}%</span>
                      <span className="text-yellow-400">Concern: {principle.concern}%</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-gray-800 rounded-full h-2">
                      <div
                        className="h-2 rounded-full"
                        style={{ width: `${principle.importance}%`, background: principle.color }}
                      ></div>
                    </div>
                    <div className="bg-gray-800 rounded-full h-2">
                      <div
                        className="bg-yellow-500 h-2 rounded-full"
                        style={{ width: `${principle.concern}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="audit" className="mt-6">
          <div className="glass-panel p-6" data-testid="audit-logs-table">
            <h3 className="text-lg font-semibold text-white mb-6">Recent Audit Logs</h3>
            <div className="space-y-3">
              {auditLogs.slice(0, 10).map((log) => (
                <div
                  key={log.id}
                  className="flex items-start gap-4 p-4 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                  data-testid={`audit-log-${log.id}`}
                >
                  <div className={`w-2 h-2 rounded-full mt-2 ${
                    log.severity === 'critical' ? 'bg-red-500' :
                    log.severity === 'warning' ? 'bg-yellow-500' : 'bg-green-500'
                  }`}></div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-white font-medium">{log.action.replace('_', ' ').toUpperCase()}</span>
                      <span className="text-gray-400 text-xs">
                        {new Date(log.timestamp).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-gray-400 text-sm">
                      User: {log.user} | Agent: {log.agent_id || 'N/A'}
                    </p>
                  </div>
                </div>
              ))}
              {auditLogs.length === 0 && (
                <p className="text-gray-400 text-center py-8">No audit logs available</p>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="compliance" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            <div className="glass-panel p-6" data-testid="compliance-frameworks">
              <h3 className="text-lg font-semibold text-white mb-6">Compliance Frameworks</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-400" />
                    <span className="text-white">SOC 2 Type II</span>
                  </div>
                  <span className="status-badge status-active">Compliant</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-400" />
                    <span className="text-white">GDPR</span>
                  </div>
                  <span className="status-badge status-active">Compliant</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-400" />
                    <span className="text-white">ISO 27001</span>
                  </div>
                  <span className="status-badge status-active">Compliant</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="w-5 h-5 text-yellow-400" />
                    <span className="text-white">HIPAA</span>
                  </div>
                  <span className="status-badge status-running">Review</span>
                </div>
              </div>
            </div>

            <div className="glass-panel p-6" data-testid="security-policies">
              <h3 className="text-lg font-semibold text-white mb-6">Security Policies</h3>
              <div className="space-y-4">
                <div className="p-3 rounded-lg bg-white/5">
                  <div className="flex items-center gap-2 mb-2">
                    <Lock className="w-4 h-4 text-cyan-400" />
                    <span className="text-white font-medium">Data Encryption</span>
                  </div>
                  <p className="text-gray-400 text-sm">End-to-end encryption enabled</p>
                </div>
                <div className="p-3 rounded-lg bg-white/5">
                  <div className="flex items-center gap-2 mb-2">
                    <Lock className="w-4 h-4 text-cyan-400" />
                    <span className="text-white font-medium">Access Control</span>
                  </div>
                  <p className="text-gray-400 text-sm">Role-based access control (RBAC)</p>
                </div>
                <div className="p-3 rounded-lg bg-white/5">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-4 h-4 text-cyan-400" />
                    <span className="text-white font-medium">Audit Logging</span>
                  </div>
                  <p className="text-gray-400 text-sm">Comprehensive logging enabled</p>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default GovernanceCenter;